const SUPER_ADMIN_CODES = ["superadmin", "super_admin"];
const MANAGEMENT_ROLE_CODES = ["admin", "cadmin", "tadmin", "tenant_admin"];
const BUSINESS_ROLE_CODES = ["operator", "observer"];
export type PrimaryRole = "admin" | "cadmin" | "tadmin" | "business" | "guest";

export const normalizeRoleCode = (value: unknown) => String(value ?? "").trim().toLowerCase();

export const normalizeRoleCodes = (roles: string[] | null | undefined) => {
  return Array.isArray(roles) ? roles.map(normalizeRoleCode).filter(Boolean) : [];
};

export const hasRoleCode = (roles: string[] | null | undefined, code: string) => {
  const normalizedCode = normalizeRoleCode(code);
  return normalizeRoleCodes(roles).includes(normalizedCode);
};

export const hasAnyRoleCode = (roles: string[] | null | undefined, codes: string[]) => {
  const normalizedRoles = normalizeRoleCodes(roles);
  return codes.some((code) => normalizedRoles.includes(normalizeRoleCode(code)));
};

export const isSuperAdminRole = (roles: string[] | null | undefined, userId?: unknown) => {
  return Number(userId) === 1 || SUPER_ADMIN_CODES.some(code => hasRoleCode(roles, code));
};

export const isPlatformAdminRole = (roles: string[] | null | undefined, userId?: unknown) => {
  return hasRoleCode(roles, "admin") || isSuperAdminRole(roles, userId);
};

export const hasManagementRole = (roles: string[] | null | undefined, userId?: unknown) => {
  return isPlatformAdminRole(roles, userId) || hasAnyRoleCode(roles, MANAGEMENT_ROLE_CODES);
};

export const hasBusinessRole = (roles: string[] | null | undefined) => {
  return hasAnyRoleCode(roles, BUSINESS_ROLE_CODES);
};

export const isBusinessRoleOnly = (roles: string[] | null | undefined, userId?: unknown) => {
  return hasBusinessRole(roles) && !hasManagementRole(roles, userId);
};

export const resolvePrimaryRole = (roles: string[] | null | undefined, userId?: unknown): PrimaryRole => {
  if (isPlatformAdminRole(roles, userId)) return "admin";
  if (hasRoleCode(roles, "cadmin")) return "cadmin";
  if (hasRoleCode(roles, "tadmin") || hasRoleCode(roles, "tenant_admin")) return "tadmin";
  if (isBusinessRoleOnly(roles, userId)) return "business";
  return "guest";
};
